// A FILE ATTACHED TO SOMETHING, as a dated timeline row (doc-attach, 2026-10-02).
//
// Any document can now be attached to a task, a note, an event or an Ongoing, and the attach shows
// up in three places that each used to drop it:
//
//   • the Ongoing's story — timeline-srv writes a `"doc"` step per (ongoing, document):
//     `EntityId` = the document, `Label` = its file name, `Detail` = the parent entity's title
//     ('' when the file went straight onto the story). `storyDocStep` turns one into a step;
//   • a thread's own items — a File item (itemType 5) on a task's / note's timeline, whose id IS the
//     document id (document-srv publishes `Id = document.Id`). `threadFileRow` reads one, and skips
//     the files that are an EMAIL MESSAGE's attachments — those are drawn under their message;
//   • the entity's own rail ("Task timeline") — one "Attachment added" line per document, dated by
//     the document list's `createdAt`. `entityAttachmentItems` builds them.
//
// Every row opens the file through Busy's `document/download/{id}` (lib/attachmentOpen.js) — a
// private upload has no public link (`webContentLink` is null), so nothing here carries a URL.
//
// Pure, over plain values, like the other rail builders: the panes only draw what comes back.

import { isImageAttachment } from '@/lib/globalTimelineFeed'
import { fileExtension } from '@/lib/briefings/entityFiles'
import { timelineDateTime } from '@/lib/timelineTime'
import { parseUtc } from '@/lib/dueStatus'
import { moduleColor } from '@/lib/moduleColors'

/** The line under the file's name, on every surface. */
export const ATTACHMENT_ADDED = 'Attachment added'

/** The accent a file row wears — the same research blue the story's file threads already use. */
export const ATTACHMENT_COLOR = moduleColor('research')

/** The File timeline item type (busy-main-bff `TimelineItemType.File`). */
export const FILE_ITEM_TYPE = 5

const nameOf = (value) => String(value || '').trim() || 'Attachment'

/** Is this file a picture — by its content type, or else its extension? */
export function isImageFile({ contentType = '', filename = '' } = {}) {
  return isImageAttachment({ contentType, extension: fileExtension(filename) })
}

/** The glyph a file row wears: `gallery` for a picture, `file-doc` for everything else. */
export function attachmentGlyph(file) {
  return isImageFile(file) ? 'gallery' : 'file-doc'
}

/** The instant a row sorts by — 0 for an undated one, so it sorts first rather than throwing. */
export function attachmentTs(value) {
  if (value === null || value === undefined || value === '') return 0
  const t = parseUtc(value).getTime()
  return Number.isFinite(t) ? t : 0
}

/**
 * One `"doc"` story event as a step both story panes draw: the file's name as the title,
 * "Attachment added" under it, and the parent entity's title as the detail line when there is one.
 * Null for anything that is not a doc event with a document behind it — a step with no document
 * would be a row that opens nothing.
 *
 * @param {object} e — a story timeline event `{ id, kind, label, detail, entityId, createdDate }`
 */
export function storyDocStep(e) {
  if (!e || e.kind !== 'doc' || !e.entityId) return null
  const name = nameOf(e.label)
  return {
    key: `doc:${e.entityId}`,
    stepKind: 'doc',
    // Something that happened ON the ongoing — it belongs on the middle as well as the rail.
    progress: true,
    svg: attachmentGlyph({ filename: name }),
    color: ATTACHMENT_COLOR,
    title: name,
    subTitle: ATTACHMENT_ADDED,
    sub: String(e.detail || '').trim(),
    docId: String(e.entityId),
    docName: name,
    expandable: false,
    _ts: attachmentTs(e.createdDate),
    ...timelineDateTime(e.createdDate),
  }
}

/**
 * A thread's File item as a row, or null when it is not one to draw: deleted, not a file, or an
 * email message's own attachment (its `fileInfo.externalEntityId` is that message's id — the
 * message row already lists it, and a second row per attachment would double every mail).
 *
 * @param {object} it — a timeline item `{ id, itemType, title, assignDate, isDeleted, fileInfo }`
 * @param {object} [tl] — the thread it is on, for its `emailMessages`
 */
export function threadFileRow(it, tl = null) {
  if (!it || it.isDeleted || it.itemType !== FILE_ITEM_TYPE || !it.id) return null
  const parent = it.fileInfo?.externalEntityId
  if (parent && (tl?.emailMessages || []).some((m) => m && String(m.id) === String(parent))) return null
  const name = nameOf(it.title || it.fileInfo?.filename)
  const contentType = String(it.fileInfo?.contentType || '')
  return {
    key: `file:${it.id}`,
    docId: String(it.id),
    name,
    contentType,
    icon: attachmentGlyph({ contentType, filename: name }),
    color: ATTACHMENT_COLOR,
    type: ATTACHMENT_ADDED,
    ts: attachmentTs(it.assignDate),
    when: it.assignDate || null,
  }
}

/**
 * The entity rail's attachment lines, oldest first: "Report.pdf / Attachment added" with the stamp
 * every rail row wears. Each carries `doc` — what the row's open / download / remove act on.
 *
 * @param {Array<object>} docs — document list rows `{ id, filename, contentType, createdAt }`
 */
export function entityAttachmentItems(docs) {
  return (docs || [])
    .filter((d) => d && d.id)
    .map((d) => {
      const name = nameOf(d.filename)
      const contentType = String(d.contentType || '')
      const isImage = isImageFile({ contentType, filename: name })
      return {
        key: `doc:${d.id}`,
        module: 'file',
        color: ATTACHMENT_COLOR,
        icon: isImage ? 'gallery' : 'file-doc',
        title: name,
        description: ATTACHMENT_ADDED,
        ...timelineDateTime(d.createdAt),
        details: [],
        // …and the timeline it hangs off, so a remove can ask the shared cache to read it again.
        doc: { id: String(d.id), name, contentType, isImage, timelineId: d.timelineId || d.TimelineId || null },
        _ts: attachmentTs(d.createdAt),
      }
    })
    .sort((a, b) => a._ts - b._ts)
}

/**
 * The entity rail with its attachment lines after its creation line. Null stays null — the rail is
 * withheld for its own reasons (lib/entityTimeline.js) and files do not overrule them.
 */
export function withAttachmentItems(rail, docs) {
  if (!rail) return null
  const extra = entityAttachmentItems(docs)
  return extra.length ? { ...rail, items: [...(rail.items || []), ...extra] } : rail
}

export default storyDocStep
